<template>
  <b-card no-body>
    <b-alert
      v-if="!hasCustomerContext"
      variant="warning"
      show
      class="m-1"
    >
      Selecciona un cliente para consultar sus razones sociales.
    </b-alert>

    <template v-else>
      <b-overlay :show="loading" rounded="sm">
        <b-table
          :items="taxInformations"
          :fields="fields"
          responsive
          empty-text="No hay razones sociales registradas."
        >
          <template #cell(index)="data">
            {{ data.index + 1 }}
          </template>

          <template #cell(corporate_name)="data">
            <span class="font-weight-bolder">{{ data.value || '-' }}</span>
          </template>

          <template #cell(int_number)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(email)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(cfdi_use)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(fiscal_regime)="data">
            {{ data.value || '-' }}
          </template>

          <template #cell(csf_document)="data">
            <b-button-group>
              <b-button
                :disabled="!data.item.csf_document"
                @click="downloadCSF(data.item)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  class="text-white"
                  size="18"
                  color="#000"
                />
              </b-button>
              <b-button
                :disabled="!data.item.csf_document"
                @click="viewCSF(data.item)"
              >
                <feather-icon
                  icon="EyeIcon"
                  class="text-white"
                  size="18"
                  color="#000"
                />
              </b-button>
            </b-button-group>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              :id="`billing-actions-${data.item.id}`"
              size="sm"
              dropright
              no-caret
              variant="outline-none"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="18"
                  color="#000"
                />
              </template>

              <b-dropdown-item @click="goToEdit(data.item)">
                {{ data.item.is_public_general ? 'Ver detalle' : $t('edit') }}
              </b-dropdown-item>
              <b-dropdown-item
                v-if="!data.item.is_public_general"
                @click="openDeleteModal(data.item)"
              >
                {{ $t('delete') }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-overlay>

      <b-pagination
        class="mt-1 ml-1"
        :value="pagination.page"
        :total-rows="pagination.total_objects"
        :per-page="pagination.per_page"
        @change="handlePageChange"
      />
    </template>

    <confirmation-modal
      :id="'confirmDeleteTaxInfo'"
      variant="danger"
      @confirm="removeTaxInformation"
    >
      <template v-slot:content>
        ¿Deseas eliminar la razón social
        <strong>{{ selectedTaxInfoToDelete.corporate_name || selectedTaxInfoToDelete.rfc }}</strong>?
      </template>
    </confirmation-modal>
  </b-card>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BTable,
  BButton,
  BButtonGroup,
  BPagination,
  BCard,
  BOverlay,
  BDropdown,
  BDropdownItem,
  BAlert,
} from 'bootstrap-vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

export default {
  components: {
    BTable,
    BButton,
    BButtonGroup,
    BPagination,
    BCard,
    BOverlay,
    BDropdown,
    BDropdownItem,
    BAlert,
    ConfirmationModal,
  },
  props: {
    customerId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      selectedTaxInfoToDelete: {},
      fields: [
        { key: 'index', label: '#' },
        { key: 'rfc', label: 'RFC' },
        { key: 'corporate_name', label: 'Razón Social' },
        { key: 'postal_code', label: 'C.P.' },
        { key: 'street', label: 'Calle' },
        { key: 'ext_number', label: 'Número Exterior' },
        { key: 'int_number', label: 'Número Interior' },
        { key: 'suburb', label: 'Colonia' },
        { key: 'locality', label: 'Localidad' },
        { key: 'city', label: 'Municipio/Ciudad' },
        { key: 'state', label: 'Estado' },
        { key: 'email', label: 'Email' },
        { key: 'cfdi_use', label: 'CFDI' },
        { key: 'fiscal_regime', label: 'Régimen' },
        { key: 'csf_document', label: 'CSF' },
        { key: 'actions', label: this.$t('actions') },
      ],
    }
  },
  computed: {
    ...mapGetters('billing', ['taxInformations', 'pagination', 'loading']),
    ...mapGetters('customers', ['selectedCustomerResourceId']),
    ...mapGetters('auth', ['currentUser']),
    resolvedCustomerId() {
      const fromRoute = this.$route.params.customerId
      const fromStore = this.selectedCustomerResourceId
      const fromScopedRole = this.currentUser?.scoped_roles?.[0]?.role_resource_id
      return this.customerId || fromRoute || fromStore || fromScopedRole || null
    },
    hasCustomerContext() {
      return !!this.resolvedCustomerId
    },
  },
  watch: {
    resolvedCustomerId: {
      immediate: true,
      handler(newValue) {
        if (!newValue) return
        this.selectCustomerId(newValue)
        this.refreshList(1)
      },
    },
  },
  methods: {
    ...mapActions('billing', ['fetchTaxInformations', 'deleteTaxInformation']),
    ...mapMutations('billing', {
      changePage: 'CHANGE_PAGE',
      setSelectedTaxInformation: 'SET_SELECTED_TAX_INFORMATION',
    }),
    ...mapMutations('customers', {
      selectCustomerId: 'SELECT_CUSTOMER_ID',
    }),
    toast(title, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: 'EditIcon',
          variant,
        },
      })
    },
    getErrorMessage(error, fallback = 'Ocurrió un error.') {
      const payload = error?.response?.data || error?.data || {}
      if (Array.isArray(payload.messages) && payload.messages.length > 0) return payload.messages[0]
      if (typeof payload.error === 'string') return payload.error
      if (typeof error?.message === 'string') return error.message
      return fallback
    },
    async refreshList(page = 1) {
      if (!this.resolvedCustomerId) return
      try {
        await this.fetchTaxInformations({
          customerId: this.resolvedCustomerId,
          pagination: {
            page,
            per_page: this.pagination.per_page || 10,
          },
        })
      } catch (error) {
        this.toast(this.getErrorMessage(error, 'No fue posible cargar las razones sociales.'), 'danger')
      }
    },
    handlePageChange(page) {
      this.changePage(page)
      this.refreshList(page)
    },
    openDeleteModal(taxInformation) {
      this.selectedTaxInfoToDelete = taxInformation
      this.$bvModal.show('confirmDeleteTaxInfo')
    },
    async removeTaxInformation() {
      if (!this.selectedTaxInfoToDelete.id || !this.resolvedCustomerId) return

      try {
        await this.deleteTaxInformation({
          customerId: this.resolvedCustomerId,
          taxInformationId: this.selectedTaxInfoToDelete.id,
        })
        this.toast('Razón social eliminada.', 'success')
        await this.refreshList(this.pagination.page || 1)
        if ((this.taxInformations || []).length === 0 && (this.pagination.page || 1) > 1) {
          await this.refreshList((this.pagination.page || 1) - 1)
        }
      } catch (error) {
        this.toast(this.getErrorMessage(error, 'No fue posible eliminar la razón social.'), 'danger')
      }
    },
    goToEdit(taxInformation) {
      this.setSelectedTaxInformation(taxInformation)
      this.$router.push({
        name: 'new-billing',
        params: this.resolvedCustomerId ? { customerId: this.resolvedCustomerId } : {},
        query: { id: taxInformation.id },
      })
    },
    downloadCSF(taxInformation) {
      if (!taxInformation.csf_document) {
        this.toast('Esta razón social no tiene constancia fiscal registrada.', 'warning')
        return
      }
      const link = document.createElement('a')
      link.href = taxInformation.csf_document
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      link.download = `CSF-${taxInformation.rfc || 'tax-information'}`
      link.click()
    },
    viewCSF(taxInformation) {
      if (!taxInformation.csf_document) {
        this.toast('Esta razón social no tiene constancia fiscal registrada.', 'warning')
        return
      }
      window.open(taxInformation.csf_document, '_blank', 'noopener,noreferrer')
    },
  },
}
</script>

<style scoped>
</style>
